import {
  displayErrorToast,
  Dropdown,
  FileDisplay,
  FileUploadButton,
  InfoCard,
  Link,
  useDialog,
  Button,
  Card,
} from "@endya-conformite/ui-kit";
import ChevronLeft from "@endya-conformite/ui-kit/src/assets/icons/chevron-left.svg?react";
import Download from "@endya-conformite/ui-kit/src/assets/icons/download.svg?react";
import Refresh from "@endya-conformite/ui-kit/src/assets/icons/refresh.svg?react";
import { PageWithTitleTemplate } from "@src/components/templates/PageWithTitleTemplate";
import { ControleQuinquennalApi } from "@src/data/controle-quinquennal/controle-quinquennal.api";
import { useCQWithAxesQuery } from "@src/data/controle-quinquennal/hook/useCQWithAxesQuery";
import { useRapportCQPreviewQuery } from "@src/data/controle-quinquennal/hook/useRapportCQPreviewQuery";
import { useValidateCQMutation } from "@src/data/controle-quinquennal/hook/useValidateCQMutation";
import { downloadFile } from "@src/helper/file.helper";
import { formatTitle } from "@src/helper/title.helper";
import { cqDossierUrl } from "@src/pages/controle-quinquennal/controleQuinquennal.helper";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { FIL_ARIANE_MES_CONTROLES } from "@src/pages/controle-quinquennal/cqListe.wording";
import { CQAxesPageContentProps } from "./CQAxesPage";
import styles from "./CQRapportPage.module.scss";

function CQRapportPageContent({ idEntreprise }: CQAxesPageContentProps) {
  const navigate = useNavigate();
  const { openConfirmDialog } = useDialog();
  const [file, setFile] = useState<File>();
  const [wasClearedByUser, setWasClearedByUser] = useState(false);
  const [isDocxLoading, setIsDocxLoading] = useState(false);
  const { data: controleQuinquennal } = useCQWithAxesQuery(idEntreprise);
  const {
    mutate: validateCQ,
    isPending,
    isSuccess,
  } = useValidateCQMutation(idEntreprise);
  const {
    data: previewBlob,
    isLoading: isPreviewLoading,
    isFetching: isPreviewFetching,
    refetch: refetchPreview,
  } = useRapportCQPreviewQuery(controleQuinquennal?.id);

  useEffect(() => {
    if (!previewBlob || file || wasClearedByUser || !controleQuinquennal) {
      return;
    }
    const filename = `rapport-${controleQuinquennal.entrepriseName}-${controleQuinquennal.millesime}.pdf`;
    setFile(new File([previewBlob], filename, { type: "application/pdf" }));
  }, [previewBlob, file, wasClearedByUser, controleQuinquennal]);

  if (!controleQuinquennal) {
    return <Navigate to="/" />;
  }

  const handleDeleteFile = () => {
    setFile(undefined);
    setWasClearedByUser(true);
  };

  const handleUploadFile = (uploaded: File) => {
    setFile(uploaded);
    setWasClearedByUser(false);
  };

  const handleDownload = () => {
    if (!file) return;
    downloadFile(file, file.name);
  };

  const handleRegenerate = () => {
    setWasClearedByUser(false);
    refetchPreview().catch(console.error);
  };

  const handleDownloadDocx = () => {
    setIsDocxLoading(true);
    ControleQuinquennalApi.getRapportPreviewDocx(controleQuinquennal.id)
      .then((blob) => {
        downloadFile(
          blob,
          `rapport-${controleQuinquennal.entrepriseName}-${controleQuinquennal.millesime}.docx`
        );
      })
      .catch(() => {
        displayErrorToast(
          "Une erreur est survenue lors de la génération du rapport Word"
        );
      })
      .finally(() => {
        setIsDocxLoading(false);
      });
  };

  const handleSend = () => {
    if (!file) return;

    openConfirmDialog({
      title: "Vous allez envoyer le rapport",
      message:
        "Le contrôle ne sera plus modifiable. Le rapport sera accessible par l'adhérent.",
      cancelText: "Annuler",
      confirmText: "Valider",
    })
      .then((shouldSend) => {
        if (!shouldSend) return;
        validateCQ({
          idCQ: controleQuinquennal.id,
          siren: idEntreprise,
          file,
        });
      })
      .catch(console.error);
  };

  const { entrepriseName, millesime } = controleQuinquennal;
  const formattedTitle = formatTitle(entrepriseName, millesime);

  const breadcrumbElements = [
    FIL_ARIANE_MES_CONTROLES,
    {
      label: formattedTitle,
      value: cqDossierUrl(idEntreprise, controleQuinquennal.id),
    },
    { label: "Dépôt du rapport de contrôle quinquennal", value: "current" },
  ];

  const isPreviewSpinning = isPreviewLoading || isPreviewFetching;

  return (
    <PageWithTitleTemplate
      title="Dépôt du rapport"
      breadcrumb={{
        items: breadcrumbElements,
        setSelected: (nextRoute) => {
          const currentRoute = breadcrumbElements.at(-1);
          if (currentRoute && nextRoute !== currentRoute.value) {
            navigate(nextRoute);
          }
        },
        selected: breadcrumbElements[2].value,
      }}
      actions={
        <div className={styles.actions}>
          <Link to={cqDossierUrl(idEntreprise, controleQuinquennal.id)}>
            <Button variant="text" icon={<ChevronLeft />}>
              Précédent
            </Button>
          </Link>
          <Button
            onClick={handleSend}
            disabled={!file || isPending || isSuccess}
          >
            Envoyer
          </Button>
        </div>
      }
    >
      <div className={styles.main}>
        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <h3>Rapport de contrôle quinquennal</h3>
            <div className={styles.cardHeaderActions}>
              {!file && (
                <Button
                  variant="text"
                  onClick={handleRegenerate}
                  icon={
                    <Refresh
                      className={classNames({
                        [styles.spinning]: isPreviewSpinning,
                      })}
                    />
                  }
                  disabled={isPreviewSpinning}
                >
                  Regénérer le rapport
                </Button>
              )}
              <Dropdown
                dismissOnClick
                variant="text"
                icon={<Download />}
                content="Télécharger"
              >
                {({ close }) => (
                  <>
                    {file && (
                      <Button
                        variant="text"
                        onClick={() => {
                          close();
                          handleDownload();
                        }}
                      >
                        PDF
                      </Button>
                    )}
                    <Button
                      variant="text"
                      disabled={isDocxLoading}
                      onClick={() => {
                        close();
                        handleDownloadDocx();
                      }}
                    >
                      Word (.docx)
                    </Button>
                  </>
                )}
              </Dropdown>
            </div>
          </div>
          <InfoCard type="info" className={styles.infoCard}>
            Ce rapport a été généré automatiquement à partir des données du
            contrôle quinquennal <b>{millesime}</b> de <b>{entrepriseName}</b>.
            Vous pouvez l&apos;envoyer en l&apos;état, ou le remplacer par votre
            propre version en le supprimant.
          </InfoCard>
          {!file && (
            <FileUploadButton
              onChange={handleUploadFile}
              disabled={isPreviewSpinning}
            />
          )}
          {file && (
            <FileDisplay fileName={file.name} onDelete={handleDeleteFile} />
          )}
        </Card>
      </div>
    </PageWithTitleTemplate>
  );
}

export function CQRapportPage() {
  const { idEntreprise } = useParams<CQAxesPageContentProps>();
  if (!idEntreprise) return <div>Impossible d&apos;afficher la page</div>;
  return <CQRapportPageContent idEntreprise={idEntreprise} />;
}
